import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useProfileContext } from "../../../contexts/ProfileContext";
import {
  DeliveryType,
  Order,
  OrderLine,
  OrderLineDesign,
  OrderLineLogo,
  OrderLineProduct,
  OrderStatus,
  Packaging,
} from "../../../core/models/entities/Order";
import { OrderDeliveryFormViewModel } from "./OrderConfirmationComponent";
import { IdLookUps } from "../../../utils/models/idLookUp";
import { getProductPrice } from "../../../core/models/entities/BaseLogo";
import { groupBy } from "../../../utils/functions/GroupBy";
import { useOrderActionHandler } from "../order-create/hooks/useOrderActionsHandler";
import { Role } from "../../../core/models/entities/Role";
import { mapToOrderLine } from "../../../data/models/mappers/mapOrder";
import { trackPurchase } from "../../../utils/functions/analytics";
import { useCartContext } from "../../../contexts/CartContext";
import { showErrorToast } from "../../components/Toast/Toast";

export interface OrderDeliveryForm {
  shipToName: string;
  shipToZip: string;
  shipToContactEmail: string;
  shipToContactPerson: string;
  shipToCity: string;
  shipToContactPhone: string;
  shipToAddress: string;
  shipToCountry: string;
  emailtrackAndTrace: string;

  isLoading: boolean;
  isFetchingDate: boolean;

  latestDeliveryDate: Date;
  // Earliest date the backend will accept for the current delivery type.
  // Tracked separately from latestDeliveryDate so user-picked later dates
  // don't ratchet the floor upward.
  minimumDeliveryDate: Date;
  deliveryBefore12PMwithGLS: boolean;

  deliveryType: DeliveryType;
  packaging: Packaging;

  orderComment: string;
  originOfDelivery: string;
  requisitionNumber: string;
}

export interface ProductGroup {
  orderlines: OrderLine[];
}

// TODO super mega deprecate these filthy models, please
export interface DeliveryTypeOption {
  key: string;
  type: DeliveryType;
  title: string;
  description: string;
  feePercentage: number;
  feeUnit: string;
  label: string;
}
export interface PackagingOption {
  key: string;
  type: Packaging;
  title: string;
  description: string;
  feeAmount: number;
  feeUnit: string;
  label: string;
}

import { useApiClient } from "../../../data/clients/ApiClient";
import { useCbsConfigRepository } from "../../../data/repositories/CbsConfigRepository";
import {
  calculateOrderCo2e,
  OrderCo2eResult,
} from "../../../utils/orderCo2e";
import { resolveVatFraction } from "../order-create/hooks/useOrderActionsHandler";

function getFallbackDate(deliveryType?: DeliveryType): Date {
  const date = new Date();
  const currentHour = date.getHours();
  const isAfter4PM = currentHour >= 16;

  let daysToAdd = 4; // Default Standard

  switch (deliveryType) {
    case DeliveryType.Express24:
      daysToAdd = isAfter4PM ? 2 : 1;
      break;
    case DeliveryType.Express48:
      daysToAdd = isAfter4PM ? 3 : 2;
      break;
    case DeliveryType.Express72:
      daysToAdd = isAfter4PM ? 4 : 3;
      break;
    default:
      // Standard or unknown: default to 4 days
      daysToAdd = 4;
      break;
  }

  date.setDate(date.getDate() + daysToAdd);

  // Weekend adjustment
  const day = date.getDay();
  if (day === 6) {
    // Saturday -> Monday
    date.setDate(date.getDate() + 2);
  } else if (day === 0) {
    // Sunday -> Monday
    date.setDate(date.getDate() + 1);
  }
  return date;
}

export function useOrderConfirmationEditViewModel(
  order: Order
): OrderDeliveryFormViewModel {
  const { t } = useTranslation();
  const { getDeliveryDatePreview } = useApiClient();
  const [orderlines, setOrderlines] = useState<OrderLine[]>([]);
  const [showCustomerPrices, setShowCustomerPrices] = useState(false);

  const { clearItems, items, checkoutItems, cartSummary, cartScope } =
    useCartContext();
  const { profile, role, defaultAddress } = useProfileContext();

  const readonly = order.status !== OrderStatus.Draft;

  const [form, setForm] = useState<OrderDeliveryForm>({
    shipToName:
      order.orderDeliveryAddress?.name ??
      profile?.shippingPreferences?.shippingName ??
      defaultAddress?.name ??
      "",
    shipToZip:
      order.orderDeliveryAddress?.zip ??
      profile?.shippingPreferences?.shippingPostCode ??
      defaultAddress?.zip ??
      "",
    shipToContactEmail:
      order.orderDeliveryAddress?.email ??
      profile?.shippingPreferences?.shippingContactEmail ??
      defaultAddress?.email ??
      "",
    shipToContactPerson:
      order.orderDeliveryAddress?.contact ??
      profile?.shippingPreferences?.shippingContactPerson ??
      defaultAddress?.contact ??
      "",
    shipToCity:
      order.orderDeliveryAddress?.city ??
      profile?.shippingPreferences?.shippingCity ??
      defaultAddress?.city ??
      "",
    shipToContactPhone:
      order.orderDeliveryAddress?.phone ??
      profile?.shippingPreferences?.shippingContactPhone ??
      defaultAddress?.phone ??
      "",
    shipToAddress:
      order.orderDeliveryAddress?.address ??
      profile?.shippingPreferences?.shippingAddress ??
      defaultAddress?.address ??
      "",
    shipToCountry:
      order.orderDeliveryAddress?.country ??
      profile?.shippingPreferences?.shippingCountry ??
      defaultAddress?.country ??
      "",
    isLoading: false,
    isFetchingDate: true,
    emailtrackAndTrace: "",
    latestDeliveryDate: getFallbackDate(order.deliveryType),
    minimumDeliveryDate: getFallbackDate(order.deliveryType),
    deliveryBefore12PMwithGLS: false,
    deliveryType: order.deliveryType ?? DeliveryType.Standard,
    packaging: order.packagingType ?? Packaging.NoPackaging,
    orderComment: order.orderComment ?? "",
    originOfDelivery: order.originOfDelivery ?? "",
    requisitionNumber: order.requisitionNumber ?? "",
  });

  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [showValidation, setShowValidation] = useState(false);

  useEffect(() => {
    setForm({
      ...form,
      latestDeliveryDate: getFallbackDate(order.deliveryType),
      minimumDeliveryDate: getFallbackDate(order.deliveryType),
      deliveryType: order.deliveryType,
      packaging: order.packagingType,
    });
  }, [order.deliveryType, order.packagingType]);

  useEffect(() => {
    // If order is already placed (readonly), do not fetch new date
    if (readonly) {
      const resolved =
        order.latestDelivery ?? getFallbackDate(form.deliveryType);
      setForm((prev) => ({
        ...prev,
        latestDeliveryDate: resolved,
        minimumDeliveryDate: resolved,
        isFetchingDate: false,
      }));
      return;
    }

    let isMounted = true;
    const fetchDate = async () => {
      setForm((prev) => ({
        ...prev,
        isFetchingDate: true,
        // Set fallback date immediately while fetching
        latestDeliveryDate: getFallbackDate(form.deliveryType),
      }));
      try {
        const productNos = order.lines
          .filter(
            (l) =>
              l.product.productType === "Logo" && l.deliveryType !== "Storage"
          )
          .map((l) => l.product?.productNo)
          .filter(Boolean) as string[];
        const orgId = order.organization.id;

        const date = await getDeliveryDatePreview(
          orgId,
          productNos,
          form.deliveryType
        );
        if (isMounted) {
          setForm((prev) => ({
            ...prev,
            latestDeliveryDate: date,
            minimumDeliveryDate: date,
            isFetchingDate: false,
          }));
        }
      } catch (e) {
        console.error("Failed to fetch delivery date preview", e);
        if (isMounted) {
          setForm((prev) => ({ ...prev, isFetchingDate: false }));
        }
      }
    };
    fetchDate();
    return () => {
      isMounted = false;
    };
  }, [
    order.organization.id,
    form.deliveryType,
    order.lines,
    order.status,
    order.latestDelivery,
    readonly,
  ]);

  // If defaultAddress loads later and form is empty, populate it
  useEffect(() => {
    if (
      defaultAddress &&
      !form.shipToName &&
      !form.shipToAddress &&
      !form.shipToCity
    ) {
      setForm((prev) => ({
        ...prev,
        shipToName: defaultAddress.name,
        shipToAddress: defaultAddress.address,
        shipToCity: defaultAddress.city,
        shipToZip: defaultAddress.zip,
        shipToContactEmail: defaultAddress.email,
        shipToContactPerson: defaultAddress.contact,
        shipToContactPhone: defaultAddress.phone,
        shipToCountry: defaultAddress.country,
      }));
    }
  }, [defaultAddress]);

  const deliveryTypeGroupList: DeliveryTypeOption[] = [
    {
      key: DeliveryType.Standard,
      type: DeliveryType.Standard,
      title: t("orderConfirmation.deliveryStandard"),
      description:
        "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Morbi sagittis, augue non ultrices mollis, lorem velit gravida nunc, ac aliquet orci tellus at tellus. Phasellus et ex a libero efficitur placerat. Morbi convallis vehicula ex.",
      feePercentage: 0,
      feeUnit: "%",
      label: t("orderConfirmation.deliveryStandard"),
    },
    {
      key: DeliveryType.Express24,
      type: DeliveryType.Express24,
      title: t("orderConfirmation.deliveryExpress24"),
      description:
        "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Morbi sagittis, augue non ultrices mollis, lorem velit gravida nunc, ac aliquet orci tellus at tellus. Phasellus et ex a libero efficitur placerat. Morbi convallis vehicula ex.",
      feePercentage: 100,
      feeUnit: "%",
      label: t("orderConfirmation.deliveryExpress24"),
    },
    {
      key: DeliveryType.Express48,
      type: DeliveryType.Express48,
      title: t("orderConfirmation.deliveryExpress48"),
      description:
        "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Morbi sagittis, augue non ultrices mollis, lorem velit gravida nunc, ac aliquet orci tellus at tellus. Phasellus et ex a libero efficitur placerat. Morbi convallis vehicula ex.",
      feePercentage: 50,
      feeUnit: "%",
      label: t("orderConfirmation.deliveryExpress48"),
    },
    {
      key: DeliveryType.Express72,
      type: DeliveryType.Express72,
      title: t("orderConfirmation.deliveryExpress72"),
      description:
        "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Morbi sagittis, augue non ultrices mollis, lorem velit gravida nunc, ac aliquet orci tellus at tellus. Phasellus et ex a libero efficitur placerat. Morbi convallis vehicula ex.",
      feePercentage: 25,
      feeUnit: "%",
      label: t("orderConfirmation.deliveryExpress72"),
    },
  ];

  const packagingGroupList: PackagingOption[] = [
    {
      key: "NoPackaging",
      type: Packaging.NoPackaging,
      title: t("orderConfirmation.noPackaging"),
      description:
        "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Morbi sagittis, augue non ultrices mollis, lorem velit gravida nunc, ac aliquet orci tellus at tellus. Phasellus et ex a libero efficitur placerat. Morbi convallis vehicula ex.",
      feeAmount: 0,
      feeUnit: "",
      label: t("orderConfirmation.noPackaging"),
    },
    {
      key: "StandardBagPackaging",
      type: Packaging.StandardBagPackaging,
      title: t("orderConfirmation.standardBagPackaging"),
      description:
        "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Morbi sagittis, augue non ultrices mollis, lorem velit gravida nunc, ac aliquet orci tellus at tellus. Phasellus et ex a libero efficitur placerat. Morbi convallis vehicula ex.",
      feeAmount: 4.5,
      feeUnit: t("orderConfirmation.feePerPcs"),
      label: t("orderConfirmation.standardBagPackaging"),
    },
    {
      key: "SpecificPackaging",
      type: Packaging.SpecificPackaging,
      title: t("orderConfirmation.specificPackaging"),
      description:
        "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Morbi sagittis, augue non ultrices mollis, lorem velit gravida nunc, ac aliquet orci tellus at tellus. Phasellus et ex a libero efficitur placerat. Morbi convallis vehicula ex.",
      feeAmount: 300,
      feeUnit: t("orderConfirmation.feePerHour"),
      label: t("orderConfirmation.specificPackaging"),
    },
  ];

  const navigate = useNavigate();

  const {
    isLoading,
    handleCommitOrder,
    handlePlaceOrder,
    handleChangeOrder,
    handleChangePackagingOrDelivery,
  } = useOrderActionHandler();

  function handleBack() {
    if (readonly) {
      navigate("/order");
    } else {
      navigate("/order/place");
    }
  }

  useEffect(() => {
    if (order != null) setOrderlines(order.lines.filter((line) => !!line));
  }, [order]);

  async function handlePackagingOrDeliveryChange(
    deliveryType: DeliveryType,
    packaging: Packaging,
    deliveryBefore12PM?: boolean
  ) {
    const newOrder = await handleChangePackagingOrDelivery(
      order,
      deliveryType,
      packaging,
      deliveryBefore12PM
    );
    setOrderlines(newOrder.lines.filter((line) => !!line));
    setForm({
      ...form,
      packaging: newOrder.packagingType,
      deliveryType: newOrder.deliveryType,
      deliveryBefore12PMwithGLS: newOrder.deliveryBefore12PMwithGLS ?? false,
    });
  }

  function handleOrderFormUpdate(newForm: OrderDeliveryForm): void {
    if (newForm.deliveryBefore12PMwithGLS !== form.deliveryBefore12PMwithGLS) {
      handlePackagingOrDeliveryChange(
        newForm.deliveryType,
        newForm.packaging,
        newForm.deliveryBefore12PMwithGLS
      );
    }
    setForm({ ...newForm });
  }

  function handleChange() {
    navigate("/order/place");
    // TODO deprecation: Disable changing order state, and just navigate to the order.
    //  It is no longer possible to have multiple draft orders, which means only the current order can be changed.
    // handleChangeOrder(order).then(() => navigate('/order/place'))
  }

  function handleConfirm() {
    const validation = validateForm(form, order);

    if (!validation.isValid) {
      setValidationErrors(validation.missingFields);
      setShowValidation(true);
      return;
    }

    // Clear validation errors if form is valid
    setValidationErrors([]);
    setShowValidation(false);

    const orgId =
      role === Role.Admin
        ? order.organization.id
        : profile?.userOrganizationInformations.organizationId!;
    const orderDeliveryPreferences = {
      address: form.shipToAddress,
      city: form.shipToCity,
      company: form.shipToName,
      contactPerson: form.shipToContactPerson,
      contactPhone: form.shipToContactPhone,
      country: form.shipToCountry,
      deliveryBefore12PMwithGLS: form.deliveryBefore12PMwithGLS,
      deliveryType: form.deliveryType,
      email: form.shipToContactEmail,
      emailtrackAndTrace: form.emailtrackAndTrace,
      latestDeliveryDate: form.latestDeliveryDate,
      orderComment: form.orderComment,
      originOfDelivery: form.originOfDelivery,
      packaging: form.packaging,
      requisitionNumber: form.requisitionNumber,
      zipCode: form.shipToZip,
      shippingService: form.deliveryBefore12PMwithGLS ? "GLS12" : "GLSNORM",
    };

    // For the locally built checkout draft (id 0), post the
    // checkout-transformed items (heating opt-out flattening, extra logos)
    // the draft was built from - not the raw cart - so the API receives
    // exactly what this page displays. Server-fetched orders never post a
    // stored checkout set: it could belong to an older checkout.
    const isLocalCheckoutDraft = order.id === 0;
    const itemsToPlace = isLocalCheckoutDraft ? (checkoutItems ?? items) : items;

    // Last-line guards for identity drift: the local draft is a snapshot,
    // and an act-as switch after it was built parks the cart it came from.
    // Placing it anyway would create an empty order (burning a NAV number)
    // or an order on the wrong organization.
    if (isLocalCheckoutDraft) {
      // Count quantities, not just lines: row-based logo types can hold
      // lines whose rows all sit at 0 (their toDelivery/toStorage aggregates
      // go stale on row edits, so the minimum-quantity validation passes),
      // and those map to an order without any real content.
      if (
        itemsToPlace.length === 0 ||
        (cartSummary?.totalQuantity ?? 0) === 0
      ) {
        showErrorToast(t("orderConfirmation.cannotPlaceEmptyOrder"));
        return;
      }
      const contextOrgId =
        profile?.userOrganizationInformations?.organizationId;
      if (contextOrgId != null && order.organization?.id !== contextOrgId) {
        showErrorToast(t("orderConfirmation.identityChangedReviewCart"));
        navigate("/order/place");
        return;
      }
    }

    // Captured at confirm time: placement takes seconds, and an act-as
    // switch while it is in flight swaps the live cart to the incoming
    // identity. Passing the scope back makes the clear land in the bucket
    // the order was actually placed from.
    const scopeAtConfirm = cartScope;
    handlePlaceOrder(orgId, orderDeliveryPreferences, itemsToPlace).then(
      (orderNumber) => {
        trackPurchase(0, orgId);
        clearItems(scopeAtConfirm);
        navigate(`/order/end?orderNumber=${orderNumber}`);
      }
    );
  }

  const pricePerProductByTotalQuantity = cachePriceBySkuQuantity(orderlines);

  function cachePriceBySkuQuantity(orderlines: OrderLine[]): IdLookUps<number> {
    let dict: IdLookUps<number> = {};

    orderlines.forEach((element) => {
      switch (element.product.productType) {
        case "Design": {
          const design = element as OrderLineDesign;
          if (dict[design.product.productMul!] == undefined) {
            dict[design.product.productMul!] = design.quantity ?? 0;
          } else {
            dict[design.product.productMul!] += design.quantity ?? 0;
          }
          return;
        }
        case "Logo": {
          const logo = element as OrderLineLogo;
          if (dict[logo.product.productSku!] == undefined) {
            dict[logo.product.productSku!] = logo.quantity ?? 0;
          } else {
            dict[logo.product.productSku!] += logo.quantity ?? 0;
          }
          return;
        }
      }
    });

    return dict;
  }

  function getQuantity(product: OrderLineProduct): number {
    const mul = product.productMul ?? "";
    const sku = product.productSku ?? "";

    switch (product.productType) {
      case "Design":
        return pricePerProductByTotalQuantity[mul];
      case "Logo":
        return pricePerProductByTotalQuantity[sku];
      default:
        return 0;
    }
  }

  const subTotal = order.lines.reduce((acc, line) => {
    const price =
      role === Role.Vendor && !showCustomerPrices
        ? line.price
        : line.adjustedPrice;

    // If the order is a draft (not placed), the price is the unit price, so we need to multiply by quantity.
    // If the order is placed (readonly), the price is the total line price, so we should NOT multiply by quantity.
    if (!readonly) {
      return acc + (price || 0) * line.quantity;
    }

    return acc + (price || 0);
  }, 0);
  // VAT preview at the org's own rate — 25% was hardcoded, which showed
  // Danish VAT to every foreign org (act-as DOS4U/NL is 21%). `||` (not ??)
  // so a missing OR zero vatRate falls back to 0.25 exactly like before:
  // the local draft's placeholder organization carries vatRate: 0, and
  // treating that as "0% VAT" would silently drop the VAT line for orgs
  // whose profile org hadn't loaded. DKK orgs (vatRate 0.25) are unchanged.
  const vatRate = resolveVatFraction(order.organization?.vatRate);
  const vat = vatRate * subTotal;
  const totalAmount = subTotal + vat;
  function validateForm(
    form: OrderDeliveryForm,
    order: Order
  ): { isValid: boolean; missingFields: string[] } {
    const missingFields: string[] = [];

    if (!form.shipToName || form.shipToName.length === 0) {
      missingFields.push("Company");
    }
    if (!form.shipToContactPerson || form.shipToContactPerson.length === 0) {
      missingFields.push("Contact Person");
    }
    if (!form.shipToAddress || form.shipToAddress.length === 0) {
      missingFields.push("Address");
    }
    if (!form.shipToZip || form.shipToZip.length === 0) {
      missingFields.push("Zip Code");
    }
    if (!form.shipToCity || form.shipToCity.length === 0) {
      missingFields.push("City");
    }
    if (!form.shipToCountry || form.shipToCountry.length === 0) {
      missingFields.push("Country");
    }
    if (!form.requisitionNumber || form.requisitionNumber.length === 0) {
      missingFields.push("Requisition Number");
    }

    // Only require "Where are the clothes sent from?" if there are Design products
    const hasDesignProducts = order.lines.some(
      (line) => line.product.productType === "Design"
    );
    if (
      hasDesignProducts &&
      (!form.originOfDelivery || form.originOfDelivery.length === 0)
    ) {
      missingFields.push("Where are the clothes sent from?");
    }

    return {
      isValid: missingFields.length === 0,
      missingFields,
    };
  }

  const formValidation = validateForm(form, order);
  const isFormValid = formValidation.isValid;

  const { useCbsConfig } = useCbsConfigRepository();
  const { data: cbsConfig } = useCbsConfig();
  let co2eEstimate: OrderCo2eResult | null = null;
  try {
    co2eEstimate = cbsConfig ? calculateOrderCo2e(order, cbsConfig) : null;
  } catch {
    co2eEstimate = null;
  }

  return {
    order,
    isLoading,
    orderNo: order.orderNumber,
    orderlines,
    deliveryTypeGroupList,
    form,
    handleBack,
    handleChange,
    handleConfirm,
    handlePackagingOrDeliveryChange,
    handleOrderFormUpdate,
    packagingGroupList,
    subTotal,
    vat,
    totalAmount,
    readonly,
    isFormValid,
    validationErrors,
    showValidation,
    showCustomerPrices,
    setShowCustomerPrices,
    co2eEstimate,
  };
}
